import { GOAL_TEMPLATE_IDS, type GoalTemplateId } from '@/features/goals/goalTemplates';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { Gift, Home, Landmark, Network, PiggyBank, Receipt, Shield, Users } from 'lucide-react';

const TEMPLATE_ICONS: Record<GoalTemplateId, typeof PiggyBank> = {
  emergency_fund: Shield,
  isa_allowance: Landmark,
  tax_reserve: Receipt,
  mortgage_payoff: Home,
  savings: PiggyBank,
  fund_family_trust: Users,
  annual_gift_allowance: Gift,
  generational_transfer: Network,
};

interface GoalTemplatePickerProps {
  selectedId?: GoalTemplateId | null;
  onSelect: (templateId: GoalTemplateId) => void;
  className?: string;
}

export function GoalTemplatePicker({ selectedId, onSelect, className }: GoalTemplatePickerProps) {
  const { t } = useLanguage();

  return (
    <div className={cn('space-y-2', className)}>
      <p className='text-muted-foreground text-sm'>{t('goals.templates.pickHint')}</p>
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        {GOAL_TEMPLATE_IDS.map((id) => {
          const Icon = TEMPLATE_ICONS[id];
          const selected = selectedId === id;
          return (
            <button
              key={id}
              type='button'
              onClick={() => onSelect(id)}
              className={cn(
                'border-border hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                selected && 'border-fin-accent bg-fin-gray-800/40 ring-fin-accent ring-1'
              )}
            >
              <div className='bg-secondary text-foreground mt-0.5 rounded-md p-2'>
                <Icon className='size-4' aria-hidden />
              </div>
              <div className='min-w-0 flex-1'>
                <div className='text-sm font-medium'>{t(`goals.templates.${id}.name`)}</div>
                <div className='text-muted-foreground line-clamp-2 text-xs'>
                  {t(`goals.templates.${id}.description`)}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
